/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { alpha, useTheme } from '@mui/material';
import useIsMobile from 'hooks/useIsMobile';
import * as d3 from 'd3';
import {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
  forwardRef,
  RefObject,
} from 'react';
import { useRecoilValue } from 'recoil';
import { negativeTrendColorState, positiveTrendColorState } from 'states';
import {
  D3PnlPlotArea,
  D3PnlPlotLine,
  OCSettingsItem,
  PnlSummary,
  PositionLeg,
  PositionShares,
  StrikeDeltaObject,
} from 'types/optionsCalculator';
import { formatAsCompactNumber, formatAsCurrency } from 'util/shared';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  formatPnlTteDateDisplay,
  formatStrikeIndicatorExpiration,
} from 'util/optionsCalculator';
import StrikeIndicator from './StrikeIndicator';
import StrikeSlot from 'components/optionsCalculator/pnlChart/StrikeSlot';
import { DEFAULT_DTE_PNL_TICK_CONFIG } from 'config/optionsCalculator';

export interface D3PlotProps {
  canvasWidth?: number;
  canvasHeight?: number;
  xDomain: number[];
  yDomain: number[];
  disabledExpirationOptions: number[];
  expirationOptions: number[];
  lines: D3PnlPlotLine[];
  referenceLines: D3PnlPlotLine[];
  areas: D3PnlPlotArea[];
  margin?: { top: number; right: number; bottom: number; left: number };
  xAxisLabel?: string;
  yAxisLabel?: string;
  summary?: PnlSummary;
  showSummary?: boolean;
  showReferenceLines?: boolean;
  defaultZoom?: { x: number; y: number; kX: number; kY: number };
  forceDefaultZoom?: boolean;
  setForceDefaultZoom?: (forceDefaultZoom: boolean) => void;
  optionLegs?: PositionLeg[];
  strikesPerExpiration?: Map<number, number[]>;
  onLegChange?: (
    legId: number,
    updates: Partial<PositionLeg>,
    shouldUseDefaultPrice: boolean,
  ) => void;
  onLegDelete?: (legId: number) => void;
  strikeDataPerExpiration?: Map<number, StrikeDeltaObject[]>;
  showStrikePicker?: boolean;
  sym?: string;
  timeToExpiration?: number;
  currentPrice?: number;
  shares: PositionShares[];
  ref: RefObject<HTMLDivElement>;
}

const D3Plot = forwardRef<HTMLDivElement, Omit<D3PlotProps, 'ref'>>(
  (
    {
      canvasWidth,
      canvasHeight,
      xDomain,
      yDomain,
      disabledExpirationOptions,
      expirationOptions,
      lines,
      areas,
      referenceLines,
      margin: marginProp,
      xAxisLabel,
      yAxisLabel,
      summary,
      showSummary = true,
      defaultZoom,
      showReferenceLines = true,
      forceDefaultZoom,
      setForceDefaultZoom,
      optionLegs = [],
      strikeDataPerExpiration = new Map<number, StrikeDeltaObject[]>(),
      onLegChange,
      onLegDelete,
      showStrikePicker = false,
      sym,
      timeToExpiration,
      currentPrice,
      shares,
    },
    ref,
  ) => {
    const theme = useTheme();
    const [isFocused, setIsFocused] = useState(false);
    const positiveColor = useRecoilValue(positiveTrendColorState);
    const negativeColor = useRecoilValue(negativeTrendColorState);
    const isMobile = useIsMobile();

    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const strikePickerRef = useRef<HTMLDivElement>(null);

    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    // TradingView-style zoom state
    const [xRange, setXRange] = useState<[number, number]>([
      xDomain[0],
      xDomain[1],
    ]);
    const [yRange, setYRange] = useState<[number, number]>([
      yDomain[0],
      yDomain[1],
    ]);
    const [zoomDir, setZoomDir] = useState<'in' | 'out'>('in');
    const zoomDirTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );

    const isDraggingRef = useRef(false);
    const dragStartRef = useRef<{
      x: number;
      y: number;
      xRange: [number, number];
      yRange: [number, number];
    } | null>(null);

    // Axis dragging state
    const isAxisDraggingRef = useRef<{
      axis: 'x' | 'y';
      startValue: number;
      startRange: [number, number];
      startMouseValue: number;
    } | null>(null);

    // Strike picker DnD state
    const [activeLeg, setActiveLeg] = useState<PositionLeg | null>(null);
    const [hoveredStrike, setHoveredStrike] = useState<number | undefined>(
      undefined,
    );

    // Unique IDs per instance (avoid clipPath collisions)
    const clipIdRef = useRef(
      `chart-clip-${Math.random().toString(36).slice(2)}`,
    );

    const margin = useMemo(
      () =>
        marginProp || {
          top: isMobile ? 60 : 26,
          right: 20,
          bottom: 65,
          left: 80,
        },
      [marginProp, isMobile],
    );

    // DnD sensors for strike picker - reduce activation distance for better responsiveness
    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 5, // minimum distance in pixels to activate the sensor
        },
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      }),
    );

    // Helper function to constrain ranges to domain limits
    const constrainRangeToDomain = useCallback(
      (range: [number, number], domain: [number, number]): [number, number] => {
        const [rangeMin, rangeMax] = range;
        const [domainMin, domainMax] = domain;
        const rangeSize = rangeMax - rangeMin;

        // If range is larger than domain, center it within domain
        if (rangeSize >= domainMax - domainMin) {
          return [domainMin, domainMax];
        }

        // If range extends beyond domain bounds, shift it to fit
        if (rangeMin < domainMin) {
          return [domainMin, domainMin + rangeSize];
        }
        if (rangeMax > domainMax) {
          return [domainMax - rangeSize, domainMax];
        }

        return range;
      },
      [],
    );

    // Handle clicks outside the chart to lose focus
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Element;

        // Check if click is outside the chart container
        if (containerRef.current && !containerRef.current.contains(target)) {
          // Exclude chart-sizing-button from being considered outside
          const isChartControl =
            target.closest(`#${OCSettingsItem.RESTORE_DEFAULT_VIEW}`) ||
            target.closest(`#${OCSettingsItem.SMOOTHING}`) ||
            target.closest(`#${OCSettingsItem.KEY_LEVELS}`) ||
            target.closest(`#${OCSettingsItem.FULLSCREEN}`);
          if (!isChartControl) {
            setIsFocused?.(false);
          }
        }
      };

      if (isFocused) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isFocused]);

    // ----- Resize handling (observe the container) -----
    useEffect(() => {
      const el =
        containerRef.current || svgRef.current?.parentElement || svgRef.current;
      if (!el) {
        return;
      }

      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          setDimensions({ width, height });
        }
      });
      ro.observe(el);
      return () => {
        ro.disconnect();
        removeTooltip(); // backstop cleanup
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Derived inner size - ensure we have proper dimensions for xScale calculation
    const width = Math.max(
      0,
      (canvasWidth || dimensions.width) - margin.left - margin.right,
    );
    const height = Math.max(
      0,
      (canvasHeight || dimensions.height) - margin.top - margin.bottom,
    );

    // Scales using current zoom ranges - ensure proper domain and range for precise positioning
    const xScale = d3
      .scaleLinear()
      .domain(xRange)
      .range([0, width])
      .clamp(false); // Don't clamp to allow precise positioning even outside visible range

    const yScale = d3.scaleLinear().domain(yRange).range([height, 0]);

    // ----- Tooltip helpers -----
    const removeTooltip = useCallback(() => {
      if (!containerRef.current) {
        return;
      }
      const t = containerRef.current.querySelector('.d3plot-tooltip');
      if (t != null) {
        t.remove();
      }
    }, []);

    // ----- Default zoom application -----
    const applyDefaultZoom = useCallback(() => {
      if (!defaultZoom) {
        return;
      }

      // Calculate zoom range based on default zoom parameters
      const fullXRange = xDomain[1] - xDomain[0];
      const fullYRange = yDomain[1] - yDomain[0];

      const zoomedXRange = fullXRange / defaultZoom.kX;
      const zoomedYRange = fullYRange / defaultZoom.kY;

      const newXRange: [number, number] = [
        defaultZoom.x - zoomedXRange / 2,
        defaultZoom.x + zoomedXRange / 2,
      ];

      const newYRange: [number, number] = [
        defaultZoom.y - zoomedYRange / 2,
        defaultZoom.y + zoomedYRange / 2,
      ];

      // Constrain to domain limits
      const constrainedXRange = constrainRangeToDomain(newXRange, [
        xDomain[0],
        xDomain[1],
      ]);
      const constrainedYRange = constrainRangeToDomain(newYRange, [
        yDomain[0],
        yDomain[1],
      ]);

      setXRange(constrainedXRange);
      setYRange(constrainedYRange);
    }, [defaultZoom, xDomain, yDomain, constrainRangeToDomain]);

    // Handle external "reset to default zoom"
    useEffect(() => {
      if (forceDefaultZoom && setForceDefaultZoom) {
        applyDefaultZoom();
        setForceDefaultZoom(false);
      }
    }, [forceDefaultZoom, applyDefaultZoom, setForceDefaultZoom]);

    // Initialize ranges when domains change
    useEffect(() => {
      if (!xRange || xRange[0] === xRange[1]) {
        setXRange([xDomain[0], xDomain[1]]);
      }
      if (!yRange || yRange[0] === yRange[1]) {
        setYRange([yDomain[0], yDomain[1]]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [xDomain, yDomain]);

    // Strike picker DnD handlers
    const handleDragStart = (event: DragStartEvent) => {
      const { active } = event;
      if (active.data.current?.type === 'leg') {
        setActiveLeg(active.data.current.leg as PositionLeg);
        // Immediately show hover state when dragging starts
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        setHoveredStrike(active.data.current.leg.strike);
      }
    };

    const handleDragOver = (event: DragOverEvent) => {
      const { over } = event;
      if (over && over.data.current?.type === 'slot') {
        setHoveredStrike(over.data.current.strike as number);
      } else if (activeLeg) {
        // Keep showing the original strike when not over a valid slot
        setHoveredStrike(activeLeg.strike);
      }
    };

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveLeg(null);
      setHoveredStrike(undefined);

      if (!over || active.id === over.id || !onLegChange) {
        return;
      }

      const activeData = active.data.current;
      const overData = over.data.current;

      // Handle leg being dropped on a slot - allow free movement to any strike
      if (activeData?.type === 'leg' && overData?.type === 'slot') {
        const legId = active.id as number;
        const newStrike = overData.strike as number;

        const targetExpiry = overData.expiry as number;

        // Allow movement to any strike for the same expiry (no restrictions)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (activeData.leg.expiry === targetExpiry) {
          onLegChange(legId, { strike: newStrike }, true);
        }
      }
    };

    // Returns cursor for the inner chart area only (axis rects override via their own CSS)
    const getChartCursor = useCallback(() => {
      if (isDraggingRef.current) {
        return 'grabbing';
      }
      if (isFocused || isMobile) {
        return zoomDir === 'out' ? 'zoom-out' : 'zoom-in';
      }
      return 'default';
    }, [isFocused, isMobile, zoomDir]);

    // TradingView-style zoom handlers
    const handleWheel = useCallback(
      (event: WheelEvent) => {
        const rect = svgRef.current!.getBoundingClientRect();
        const mouseX = event.clientX - rect.left - margin.left;
        const mouseY = event.clientY - rect.top - margin.top;

        // Check if mouse is over the X-axis area (including bottom margin)
        if (
          mouseY >= height &&
          mouseY <= height + margin.bottom &&
          mouseX >= 0 &&
          mouseX <= width
        ) {
          // X-axis wheel zoom - always allowed
          event.preventDefault();
          const zoomFactor = event.deltaY > 0 ? 1.2 : 1 / 1.2;
          const mouseXValue = xScale.invert(mouseX);

          const currentXRange = xRange[1] - xRange[0];
          const newXRange = currentXRange * zoomFactor;

          const mouseXRatio = (mouseXValue - xRange[0]) / currentXRange;
          const newXMin = mouseXValue - newXRange * mouseXRatio;
          const newXMax = mouseXValue + newXRange * (1 - mouseXRatio);

          // Constrain to original domain
          const constrainedRange = constrainRangeToDomain(
            [newXMin, newXMax],
            [xDomain[0], xDomain[1]],
          );
          setXRange(constrainedRange);
          return;
        }

        // Check if mouse is over the Y-axis area (including left margin)
        if (
          mouseX >= -margin.left &&
          mouseX <= 0 &&
          mouseY >= 0 &&
          mouseY <= height
        ) {
          // Y-axis wheel zoom - always allowed
          event.preventDefault();
          const zoomFactor = event.deltaY > 0 ? 1.2 : 1 / 1.2;
          const mouseYValue = yScale.invert(mouseY);

          const currentYRange = yRange[1] - yRange[0];
          const newYRange = currentYRange * zoomFactor;

          const mouseYRatio = (mouseYValue - yRange[0]) / currentYRange;
          const newYMin = mouseYValue - newYRange * mouseYRatio;
          const newYMax = mouseYValue + newYRange * (1 - mouseYRatio);

          // Constrain to original domain
          const constrainedRange = constrainRangeToDomain(
            [newYMin, newYMax],
            [yDomain[0], yDomain[1]],
          );
          setYRange(constrainedRange);
          return;
        }

        // Only zoom chart area if focused or mobile
        if (!isFocused && !isMobile) {
          return;
        }

        // Only zoom if mouse is within chart area (default behavior)
        if (mouseX < 0 || mouseX > width || mouseY < 0 || mouseY > height) {
          return;
        }

        event.preventDefault();

        // Update cursor to reflect zoom direction; reset after scrolling stops
        setZoomDir(event.deltaY > 0 ? 'out' : 'in');
        if (zoomDirTimeoutRef.current) {
          clearTimeout(zoomDirTimeoutRef.current);
        }
        zoomDirTimeoutRef.current = setTimeout(() => setZoomDir('in'), 400);

        const zoomFactor = event.deltaY > 0 ? 1.2 : 1 / 1.2;
        const mouseXValue = xScale.invert(mouseX);

        // TradingView style: zoom X-axis around mouse position
        const currentXRange = xRange[1] - xRange[0];
        const newXRange = currentXRange * zoomFactor;

        const mouseXRatio = (mouseXValue - xRange[0]) / currentXRange;
        const newXMin = mouseXValue - newXRange * mouseXRatio;
        const newXMax = mouseXValue + newXRange * (1 - mouseXRatio);

        // Constrain to original domain
        const constrainedRange = constrainRangeToDomain(
          [newXMin, newXMax],
          [xDomain[0], xDomain[1]],
        );
        setXRange(constrainedRange);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        isFocused,
        isMobile,
        height,
        margin.left,
        margin.bottom,
        width,
        xScale,
        xRange,
        xDomain,
        yScale,
        yRange,
        yDomain,
        constrainRangeToDomain,
      ],
    );

    // Axis drag is wired directly on the axis rects via D3 in the render effect
    // so this handler only manages chart-area pan.
    const handleMouseDown = useCallback(
      (event: MouseEvent) => {
        if (!svgRef.current) {
          return;
        }

        const rect = svgRef.current.getBoundingClientRect();
        const mouseX = event.clientX - rect.left - margin.left;
        const mouseY = event.clientY - rect.top - margin.top;

        // Only pan if mouse is within chart area
        if (mouseX < 0 || mouseX > width || mouseY < 0 || mouseY > height) {
          return;
        }

        // Clicking inside the chart also focuses it so scroll zoom activates
        setIsFocused(true);

        isDraggingRef.current = true;
        dragStartRef.current = {
          x: event.clientX,
          y: event.clientY,
          xRange: [...xRange],
          yRange: [...yRange],
        };

        event.preventDefault();
      },
      [height, margin.left, margin.top, width, xRange, yRange],
    );

    const handleMouseMove = useCallback(
      (event: MouseEvent) => {
        // Handle axis dragging
        if (isAxisDraggingRef.current) {
          const { axis, startValue, startRange, startMouseValue } =
            isAxisDraggingRef.current;

          if (axis === 'x') {
            // X-axis dragging: zoom behavior like wheel zoom
            const deltaX = event.clientX - startMouseValue;
            const zoomFactor = Math.max(0.1, 1 + deltaX * 0.01); // Sensitivity adjustment

            const currentXRange = startRange[1] - startRange[0];
            const newXRange = currentXRange * zoomFactor;

            // Zoom around the clicked value (like wheel zoom)
            const clickRatio = (startValue - startRange[0]) / currentXRange;
            const newXMin = startValue - newXRange * clickRatio;
            const newXMax = startValue + newXRange * (1 - clickRatio);

            // Constrain to original domain
            const constrainedRange = constrainRangeToDomain(
              [newXMin, newXMax],
              [xDomain[0], xDomain[1]],
            );
            setXRange(constrainedRange);
          } else if (axis === 'y') {
            // Y-axis dragging: mouse movement controls range scaling around clicked point
            const deltaY = event.clientY - startMouseValue;
            const scaleFactor = Math.max(0.1, 1 + deltaY * 0.01); // Sensitivity adjustment

            const rangeSize = startRange[1] - startRange[0];
            const newRangeSize = rangeSize * scaleFactor;

            // Scale around the clicked value
            const clickRatio = (startValue - startRange[0]) / rangeSize;
            const newMin = startValue - newRangeSize * clickRatio;
            const newMax = startValue + newRangeSize * (1 - clickRatio);

            // Constrain to original domain
            const constrainedRange = constrainRangeToDomain(
              [newMin, newMax],
              [yDomain[0], yDomain[1]],
            );
            setYRange(constrainedRange);
          }
          return;
        }

        // Handle regular chart panning
        if (!isDraggingRef.current || !dragStartRef.current) {
          return;
        }

        const deltaX = event.clientX - dragStartRef.current.x;
        const deltaY = event.clientY - dragStartRef.current.y;

        // Convert pixel deltas to data deltas
        const xDataDelta =
          (deltaX / width) *
          (dragStartRef.current.xRange[1] - dragStartRef.current.xRange[0]);
        const yDataDelta =
          (deltaY / height) *
          (dragStartRef.current.yRange[1] - dragStartRef.current.yRange[0]);

        // TradingView style: pan in opposite direction of mouse movement
        const newXMin = dragStartRef.current.xRange[0] - xDataDelta;
        const newXMax = dragStartRef.current.xRange[1] - xDataDelta;
        const newYMin = dragStartRef.current.yRange[0] + yDataDelta; // Y is inverted
        const newYMax = dragStartRef.current.yRange[1] + yDataDelta;

        // Constrain to domain limits
        const constrainedXRange = constrainRangeToDomain(
          [newXMin, newXMax],
          [xDomain[0], xDomain[1]],
        );
        const constrainedYRange = constrainRangeToDomain(
          [newYMin, newYMax],
          [yDomain[0], yDomain[1]],
        );

        setXRange(constrainedXRange);
        setYRange(constrainedYRange);
      },

      [width, height, xDomain, yDomain, constrainRangeToDomain],
    );

    const handleMouseUp = useCallback(() => {
      isDraggingRef.current = false;
      dragStartRef.current = null;
      isAxisDraggingRef.current = null;
    }, []);

    // Touch start — mirrors handleMouseDown for mobile axis zoom and chart pan
    const handleTouchStart = useCallback(
      (event: TouchEvent) => {
        if (!svgRef.current || event.touches.length !== 1) {
          return;
        }

        const touch = event.touches[0];
        const rect = svgRef.current.getBoundingClientRect();
        const touchX = touch.clientX - rect.left - margin.left;
        const touchY = touch.clientY - rect.top - margin.top;

        // X-axis area: drag zooms X axis
        if (
          touchY >= height &&
          touchY <= height + margin.bottom &&
          touchX >= 0 &&
          touchX <= width
        ) {
          isAxisDraggingRef.current = {
            axis: 'x',
            startValue: xScale.invert(touchX),
            startRange: [...xRange] as [number, number],
            startMouseValue: touch.clientX,
          };
          event.preventDefault();
          return;
        }

        // Y-axis area: drag zooms Y axis
        if (
          touchX >= -margin.left &&
          touchX <= 0 &&
          touchY >= 0 &&
          touchY <= height
        ) {
          isAxisDraggingRef.current = {
            axis: 'y',
            startValue: yScale.invert(touchY),
            startRange: [...yRange] as [number, number],
            startMouseValue: touch.clientY,
          };
          event.preventDefault();
          return;
        }

        // Chart area: drag pans (always enabled on mobile)
        if (touchX >= 0 && touchX <= width && touchY >= 0 && touchY <= height) {
          isDraggingRef.current = true;
          dragStartRef.current = {
            x: touch.clientX,
            y: touch.clientY,
            xRange: [...xRange] as [number, number],
            yRange: [...yRange] as [number, number],
          };
          event.preventDefault();
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [height, margin, width, xScale, xRange, yScale, yRange],
    );

    // Touch move — mirrors handleMouseMove for mobile axis zoom and chart pan
    const handleTouchMove = useCallback(
      (event: TouchEvent) => {
        if (event.touches.length !== 1) {
          return;
        }

        const touch = event.touches[0];

        if (isAxisDraggingRef.current) {
          const { axis, startValue, startRange, startMouseValue } =
            isAxisDraggingRef.current;

          if (axis === 'x') {
            const deltaX = touch.clientX - startMouseValue;
            const zoomFactor = Math.max(0.1, 1 + deltaX * 0.01);
            const currentXRange = startRange[1] - startRange[0];
            const newXRange = currentXRange * zoomFactor;
            const clickRatio = (startValue - startRange[0]) / currentXRange;
            const newXMin = startValue - newXRange * clickRatio;
            const newXMax = startValue + newXRange * (1 - clickRatio);
            setXRange(
              constrainRangeToDomain(
                [newXMin, newXMax],
                [xDomain[0], xDomain[1]],
              ),
            );
          } else {
            const deltaY = touch.clientY - startMouseValue;
            const scaleFactor = Math.max(0.1, 1 + deltaY * 0.01);
            const rangeSize = startRange[1] - startRange[0];
            const newRangeSize = rangeSize * scaleFactor;
            const clickRatio = (startValue - startRange[0]) / rangeSize;
            const newMin = startValue - newRangeSize * clickRatio;
            const newMax = startValue + newRangeSize * (1 - clickRatio);
            setYRange(
              constrainRangeToDomain(
                [newMin, newMax],
                [yDomain[0], yDomain[1]],
              ),
            );
          }
          event.preventDefault();
          return;
        }

        if (!isDraggingRef.current || !dragStartRef.current) {
          return;
        }

        const deltaX = touch.clientX - dragStartRef.current.x;
        const deltaY = touch.clientY - dragStartRef.current.y;

        const xDataDelta =
          (deltaX / width) *
          (dragStartRef.current.xRange[1] - dragStartRef.current.xRange[0]);
        const yDataDelta =
          (deltaY / height) *
          (dragStartRef.current.yRange[1] - dragStartRef.current.yRange[0]);

        setXRange(
          constrainRangeToDomain(
            [
              dragStartRef.current.xRange[0] - xDataDelta,
              dragStartRef.current.xRange[1] - xDataDelta,
            ],
            [xDomain[0], xDomain[1]],
          ),
        );
        setYRange(
          constrainRangeToDomain(
            [
              dragStartRef.current.yRange[0] + yDataDelta,
              dragStartRef.current.yRange[1] + yDataDelta,
            ],
            [yDomain[0], yDomain[1]],
          ),
        );
        event.preventDefault();
      },
      [width, height, xDomain, yDomain, constrainRangeToDomain],
    );

    const handleTouchEnd = useCallback(() => {
      isDraggingRef.current = false;
      dragStartRef.current = null;
      isAxisDraggingRef.current = null;
    }, []);

    // Event listeners
    useEffect(() => {
      const svg = svgRef.current;
      if (!svg) {
        return;
      }

      svg.addEventListener('wheel', handleWheel);
      // Touch events registered on SVG with passive:false so preventDefault works (blocks page scroll)
      svg.addEventListener('touchstart', handleTouchStart, { passive: false });
      svg.addEventListener('touchmove', handleTouchMove, { passive: false });
      svg.addEventListener('touchend', handleTouchEnd);
      document.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        svg.removeEventListener('wheel', handleWheel);
        svg.removeEventListener('touchstart', handleTouchStart);
        svg.removeEventListener('touchmove', handleTouchMove);
        svg.removeEventListener('touchend', handleTouchEnd);
        document.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }, [
      handleWheel,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
    ]);

    useEffect(() => {
      return () => {
        if (zoomDirTimeoutRef.current) {
          clearTimeout(zoomDirTimeoutRef.current);
        }
      };
    }, []);

    // Get all unique expiries from option legs
    const uniqueExpiries = Array.from(
      new Set(optionLegs.map((leg) => leg.expiry)),
    );

    // Show all available strikes for the active leg's expiry (no filtering by visibility)
    const availableStrikesByExpiry = new Map<number, number[]>();
    uniqueExpiries.forEach((expiry) => {
      const strikeObjects = strikeDataPerExpiration.get(expiry) || [];
      availableStrikesByExpiry.set(
        expiry,
        strikeObjects.map((object) => object.strike),
      );
    });

    // Group legs by strike and position type for stacking
    const groupLegsByStrikeAndType = (legs: PositionLeg[]) => {
      const groups = new Map<string, PositionLeg[]>();
      legs.forEach((leg) => {
        const key = `${leg.strike}-${leg.expiry}`;
        if (!groups.has(key)) {
          groups.set(key, []);
        }
        groups.get(key)!.push(leg);
      });
      return groups;
    };

    // Separate legs by position type (bought vs sold) and group by strike
    const boughtLegs = optionLegs.filter((leg) => leg.quantity > 0);
    const soldLegs = optionLegs.filter((leg) => leg.quantity < 0);

    const boughtLegGroups = groupLegsByStrikeAndType(boughtLegs);
    const soldLegGroups = groupLegsByStrikeAndType(soldLegs);

    // Get the latest leg expiration
    const latestLegExpiration = useMemo(() => {
      return optionLegs.length === 0
        ? 0
        : Math.min(...optionLegs.map((leg) => leg.expiry));
    }, [optionLegs]);

    // ----- Main render effect -----
    useEffect(() => {
      if (!svgRef.current || width <= 0 || height <= 0) {
        return;
      }

      const svg = d3.select(svgRef.current);
      const clipId = clipIdRef.current;

      svg.selectAll('*').remove();
      removeTooltip();

      const xAxis = d3.axisBottom(xScale);
      const yAxis = d3.axisLeft(yScale);

      // defs / clip
      svg
        .append('defs')
        .append('clipPath')
        .attr('id', clipId)
        .append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', width)
        .attr('height', height);

      // chart area
      const chartArea = svg
        .append('g')
        .attr('transform', `translate(${margin.left}, ${margin.top})`)
        .attr('clip-path', `url(#${clipId})`);

      // grids
      chartArea
        .selectAll('.x-grid-line')
        .data(xScale.ticks())
        .enter()
        .append('line')
        .attr('class', 'x-grid-line')
        .attr('x1', (d) => xScale(d))
        .attr('y1', 0)
        .attr('x2', (d) => xScale(d))
        .attr('y2', height)
        .attr('stroke', theme.palette.text.disabled)
        .attr('stroke-width', 0.5)
        .attr('opacity', 0.5);

      chartArea
        .selectAll('.y-grid-line')
        .data(yScale.ticks())
        .enter()
        .append('line')
        .attr('class', 'y-grid-line')
        .attr('x1', 0)
        .attr('y1', (d) => yScale(d))
        .attr('x2', width)
        .attr('y2', (d) => yScale(d))
        .attr('stroke', theme.palette.text.disabled)
        .attr('stroke-width', 0.5)
        .attr('opacity', 0.5);

      // areas
      areas.forEach((area) => {
        const areaGen = d3
          .area<[number, number, number]>()
          .x((d) => xScale(d[0]))
          .y0((d) => yScale(d[1]))
          .y1((d) => yScale(d[2]));
        chartArea
          .append('path')
          .datum(area.data as [number, number, number][])
          .attr('d', areaGen)
          .style('fill', area.fill)
          .style('opacity', area.opacity);
      });

      // lines
      lines.forEach((line) => {
        const lineGen = d3
          .line<[number, number]>()
          .x((d) => xScale(d[0]))
          .y((d) => yScale(d[1]));
        chartArea
          .append('path')
          .datum(line.data.map<[number, number]>((d) => [d.x, d.y]))
          .attr('d', lineGen)
          .attr('stroke', line.stroke)
          .attr('fill', line.fill || 'none')
          .attr('stroke-dasharray', line.strokeDasharray || 'none')
          .attr('opacity', line.opacity ?? 1);
      });

      // reference lines
      if (showReferenceLines && referenceLines?.length) {
        const sorted = [...referenceLines].sort(
          (a, b) => (b.label?.length ?? 0) - (a.label?.length ?? 0),
        );
        sorted.forEach((refLine, i) => {
          const { data, label, stroke } = refLine;
          if (!data?.length) {
            return;
          }
          const value = data[0].x;

          // Only draw if line is visible in current x range
          if (value < xRange[0] || value > xRange[1]) {
            return;
          }

          const xRangeSize = xRange[1] - xRange[0];
          const pxPerTick = width / xRangeSize;
          const pxFromMax = (xRange[1] - value) * pxPerTick;

          const levelsCountRatio = Math.min(1, 5 / referenceLines.length);
          const y =
            20 +
            i *
              Math.max(
                ((55 * window.innerHeight) / 1_000) * levelsCountRatio,
                20,
              );

          const text = `${label || 'Reference'} $${value}`;
          const estimatedTextWidth = text.length * 8;
          const xOff =
            estimatedTextWidth > pxFromMax ? -estimatedTextWidth * 0.82 : 10;

          chartArea
            .append('line')
            .attr('x1', xScale(value))
            .attr('x2', xScale(value))
            .attr('y1', 0)
            .attr('y2', height)
            .attr('stroke', stroke || theme.palette.primary.main)
            .attr('stroke-width', 2)
            .attr('stroke-dasharray', refLine.strokeDasharray || 'none')
            .attr('opacity', refLine.opacity ?? 0.8);

          if (label) {
            chartArea
              .append('text')
              .attr('x', xScale(value) + xOff)
              .attr('y', Math.min(y, height - 10))
              .attr('text-anchor', xOff < 0 ? 'end' : 'start')
              .style('font-size', '13px')
              .style('font-weight', '900')
              .style('fill', stroke || theme.palette.primary.main)
              .style(
                'text-shadow',
                `-1px -1px 0 ${theme.palette.background.paper}, 1px -1px 0 ${theme.palette.background.paper}, -1px 1px 0 ${theme.palette.background.paper}, 1px 1px 0 ${theme.palette.background.paper}`,
              )
              .text(text);
          }
        });
      }

      // breakeven markers
      if (summary?.breakEvenPrices?.length) {
        summary.breakEvenPrices.forEach((price) => {
          // Only draw if visible in current range
          if (price < xRange[0] || price > xRange[1]) {
            return;
          }

          chartArea
            .append('line')
            .attr('x1', xScale(price))
            .attr('x2', xScale(price))
            .attr('y1', 0)
            .attr('y2', height)
            .attr('stroke', theme.palette.text.secondary)
            .attr('stroke-width', 1)
            .attr('opacity', 0.8);

          chartArea
            .append('text')
            .attr('x', xScale(price))
            .attr('y', 15)
            .attr('text-anchor', 'middle')
            .style('font-size', '10px')
            .style('font-weight', '600')
            .style('font-family', 'monospace')
            .style('fill', theme.palette.text.primary)
            .text(`${formatAsCurrency(price)}`);
        });
      }

      // axes
      const xAxisGroup = svg
        .append('g')
        .attr(
          'transform',
          `translate(${margin.left}, ${margin.top + height + 1})`,
        )
        .call(xAxis);
      xAxisGroup
        .selectAll('text')
        .style('fill', theme.palette.text.secondary)
        .style('font-size', '12px');
      xAxisGroup
        .selectAll('line')
        .style('stroke', theme.palette.text.secondary);
      xAxisGroup
        .select('.domain')
        .style('stroke', theme.palette.text.secondary);

      // Add axis drag areas for X-axis - always enabled
      svg
        .append('rect')
        .attr('x', margin.left)
        .attr('y', margin.top + height)
        .attr('width', width)
        .attr('height', margin.bottom)
        .style('fill', 'transparent')
        .style('cursor', 'ew-resize')
        .style('pointer-events', 'all')
        .on('mousedown', (event: MouseEvent) => {
          const svgEl = svgRef.current;
          if (!svgEl) {
            return;
          }
          const svgRect = svgEl.getBoundingClientRect();
          const mouseX = event.clientX - svgRect.left - margin.left;
          isAxisDraggingRef.current = {
            axis: 'x',
            startValue: xScale.invert(mouseX),
            startRange: [...xRange] as [number, number],
            startMouseValue: event.clientX,
          };
          event.preventDefault();
          event.stopPropagation();
        });

      if (xAxisLabel) {
        svg
          .append('text')
          .attr('class', 'x-axis-label')
          .attr('text-anchor', 'middle')
          .attr('x', margin.left + width / 2)
          .attr('y', margin.top + height + margin.bottom - 10)
          .style('font-size', '12px')
          .style('font-weight', '500')
          .style('fill', theme.palette.text.secondary)
          .text(xAxisLabel);
      }

      const yAxisGroup = svg
        .append('g')
        .attr('transform', `translate(${margin.left}, ${margin.top})`)
        .call(yAxis);
      yAxisGroup
        .selectAll('text')
        .style('fill', theme.palette.text.secondary)
        .style('font-size', '12px');
      yAxisGroup
        .selectAll('line')
        .style('stroke', theme.palette.text.secondary);
      yAxisGroup
        .select('.domain')
        .style('stroke', theme.palette.text.secondary);

      // Add axis drag areas for Y-axis - always enabled
      svg
        .append('rect')
        .attr('x', 0)
        .attr('y', margin.top)
        .attr('width', margin.left)
        .attr('height', height)
        .style('fill', 'transparent')
        .style('cursor', 'ns-resize')
        .style('pointer-events', 'all')
        .on('mousedown', (event: MouseEvent) => {
          const svgEl = svgRef.current;
          if (!svgEl) {
            return;
          }
          const svgRect = svgEl.getBoundingClientRect();
          const mouseY = event.clientY - svgRect.top - margin.top;
          isAxisDraggingRef.current = {
            axis: 'y',
            startValue: yScale.invert(mouseY),
            startRange: [...yRange] as [number, number],
            startMouseValue: event.clientY,
          };
          event.preventDefault();
          event.stopPropagation();
        });

      if (yAxisLabel) {
        svg
          .append('text')
          .attr('class', 'y-axis-label')
          .attr('text-anchor', 'middle')
          .attr('transform', 'rotate(-90)')
          .attr('x', -(margin.top + height / 2))
          .attr('y', margin.left / 3)
          .style('font-size', '12px')
          .style('font-weight', '500')
          .style('fill', theme.palette.text.secondary)
          .text(yAxisLabel);
      }

      // Legend for lines
      if (lines.length > 0 && lines.some((line) => line.label)) {
        const legendGroup = svg
          .append('g')
          .attr('class', 'legend')
          .attr(
            'transform',
            `translate(${margin.left + 10}, ${margin.top + 10})`,
          );

        // Create legend background
        const legendItems = lines.filter((line) => line.label);
        const itemWidth = 120;
        const itemHeight = 20;
        const itemsPerRow = Math.floor((width - 20) / itemWidth);
        const rows = Math.ceil(legendItems.length / itemsPerRow);
        const legendWidth = Math.min(
          legendItems.length * itemWidth,
          width - 20,
        );
        const legendHeight = rows * itemHeight + 10;

        legendGroup
          .append('rect')
          .attr('x', -5)
          .attr('y', -5)
          .attr('width', legendWidth + 10)
          .attr('height', legendHeight)
          .attr('rx', 4)
          .style('fill', alpha(theme.palette.background.paper, 0.95))
          .style('stroke', alpha(theme.palette.primary.main, 0.25))
          .style('stroke-width', 1);

        legendItems.forEach((line, index) => {
          const row = Math.floor(index / itemsPerRow);
          const col = index % itemsPerRow;
          const x = col * itemWidth;
          const y = row * itemHeight + 10;

          const legendItem = legendGroup
            .append('g')
            .attr('transform', `translate(${x}, ${y})`);

          // Line sample
          const lineElement = legendItem
            .append('line')
            .attr('x1', 5)
            .attr('y1', 0)
            .attr('x2', 25)
            .attr('y2', 0)
            .attr('stroke', line.stroke)
            .attr('stroke-width', 2)
            .attr('opacity', line.opacity ?? 1);

          if (line.strokeDasharray) {
            lineElement.attr('stroke-dasharray', line.strokeDasharray);
          }

          // Label text
          legendItem
            .append('text')
            .attr('x', 30)
            .attr('y', 0)
            .attr('dy', '0.35em')
            .style('font-size', '11px')
            .style('font-weight', '500')
            .style('fill', theme.palette.text.primary)
            .text(line.label || '');
        });
      }

      // summary
      if (summary && showSummary) {
        const summaryGroup = svg
          .append('g')
          .attr('class', 'summary-panel')
          .attr(
            'transform',
            `translate(${margin.left + width / 2}, ${margin.top - (isMobile ? 50 : 16)})`,
          )
          .style('pointer-events', 'none');

        const parts: { label: string; value: string; color: string }[] = [];

        // Add symbol if available
        if (sym) {
          parts.push({
            label: 'Symbol:',
            value: sym,
            color: theme.palette.text.primary,
          });
        }

        // Calculate total credit/cost of legs and shares
        const legsCost = optionLegs.reduce((sum, leg) => {
          // For options: negative quantity means selling (credit), positive means buying (debit)
          // price_paid should be positive for premiums received/paid
          const cost = leg.price_paid * 100 * leg.quantity;
          return sum + cost;
        }, 0);

        const sharesCost = shares.reduce((sum, share) => {
          // For shares: cost is price_paid * quantity (positive for long, negative for short)
          return sum + share.price_paid * share.quantity;
        }, 0);

        const totalCost = legsCost + sharesCost;

        // If total cost is negative, it means we received a net credit
        // If total cost is positive, it means we paid a net debit
        const isCredit = totalCost < 0;
        parts.push({
          label: isCredit ? 'Net Credit:' : 'Net Debit:',
          value: formatAsCurrency(Math.abs(totalCost)),
          color: isCredit ? positiveColor : negativeColor,
        });

        // Add time to expiration if available
        if (timeToExpiration != null) {
          const dteText = formatPnlTteDateDisplay(
            timeToExpiration,
            latestLegExpiration,
            DEFAULT_DTE_PNL_TICK_CONFIG,
            isMobile,
          );
          parts.push({
            label: isMobile ? 'Date:' : 'Projection Date:',
            value: dteText,
            color: theme.palette.text.primary,
          });
        }

        if (currentPrice != null) {
          parts.push({
            label: 'Current Price:',
            value: formatAsCurrency(currentPrice),
            color: theme.palette.text.primary,
          });
        }

        if (summary.totalDelta != null) {
          parts.push({
            label: 'Current Delta:',
            value: formatAsCompactNumber(summary.totalDelta, {
              maximumFractionDigits: 3,
            }),
            color: summary.totalDelta > 0 ? positiveColor : negativeColor,
          });
        }

        if (summary.maxProfit !== undefined) {
          const v =
            summary.maxProfit === Infinity
              ? '∞'
              : `${formatAsCurrency(summary.maxProfit)}`;
          parts.push({
            label: isMobile ? 'Max P:' : 'Max Profit:',
            value: v,
            color: positiveColor,
          });
        }
        if (summary.maxLoss !== undefined) {
          const v =
            summary.maxLoss === -Infinity
              ? '-∞'
              : `${formatAsCurrency(summary.maxLoss)}`;
          parts.push({
            label: isMobile ? 'Max L:' : 'Max Loss:',
            value: v,
            color: negativeColor,
          });
        }

        if (parts.length) {
          if (isMobile && parts.length > 3) {
            // For mobile with many items, split into multiple lines
            const maxItemsPerLine = 2;
            const lines = [];
            for (let i = 0; i < parts.length; i += maxItemsPerLine) {
              lines.push(parts.slice(i, i + maxItemsPerLine));
            }

            lines.forEach((lineParts, lineIndex) => {
              const textGroup = summaryGroup
                .append('g')
                .attr('transform', `translate(0, ${lineIndex * 15})`); // 15px line height

              let currentX = 0;
              const gap = 12; // Gap between parts in pixels
              const textGap = 3; // Gap between text and value

              lineParts.forEach((p) => {
                const partGroup = textGroup.append('g');

                // Add label
                const label = partGroup
                  .append('text')
                  .attr('x', currentX)
                  .attr('y', 0)
                  .attr('text-anchor', 'start')
                  .style('font-size', '10px')
                  .style('font-weight', '500')
                  .style('letter-spacing', '0.5px')
                  .style('fill', theme.palette.text.secondary)
                  .text(p.label + ' ');

                currentX += (label.node()?.getBBox().width || 0) + textGap;

                // Add value
                const value = partGroup
                  .append('text')
                  .attr('x', currentX)
                  .attr('y', 0)
                  .attr('text-anchor', 'start')
                  .style('font-size', '11px')
                  .style('font-weight', '500')
                  .style('letter-spacing', '0.5px')
                  .style('fill', p.color)
                  .text(p.value);

                currentX += (value.node()?.getBBox().width || 0) + gap;
              });

              // Center the line
              const totalWidth = currentX - gap; // Subtract last gap
              textGroup.attr(
                'transform',
                `translate(${-totalWidth / 2}, ${lineIndex * 15})`,
              );
            });
          } else {
            // Single line for desktop or mobile with few items
            const textGroup = summaryGroup
              .append('g')
              .attr('transform', 'translate(0, 0)');

            let currentX = 0;
            const gap = 12; // Gap between parts in pixels
            const textGap = 4; // Gap between text and value

            parts.forEach((p) => {
              const partGroup = textGroup.append('g');

              // Add label
              const label = partGroup
                .append('text')
                .attr('x', currentX)
                .attr('y', 0)
                .attr('text-anchor', 'start')
                .style('font-size', isMobile ? '10px' : '12px')
                .style('font-weight', '500')
                .style('letter-spacing', '0.5px')
                .style('fill', theme.palette.text.secondary)
                .text(p.label + ' ');

              currentX += (label.node()?.getBBox().width || 0) + textGap;

              // Add value
              const value = partGroup
                .append('text')
                .attr('x', currentX)
                .attr('y', 0)
                .attr('text-anchor', 'start')
                .style('font-size', isMobile ? '11px' : '13px')
                .style('font-weight', '500')
                .style('letter-spacing', '0.5px')
                .style('fill', p.color)
                .text(p.value);

              currentX += (value.node()?.getBBox().width || 0) + gap;
            });

            // Center the entire group
            const totalWidth = currentX - gap; // Subtract last gap
            textGroup.attr('transform', `translate(${-totalWidth / 2}, 0)`);
          }
        }
      }

      // ----- Tooltip (scoped to container) -----
      const tooltip = d3
        .select(containerRef.current)
        .append('div')
        .attr('class', 'd3plot-tooltip')
        .style('opacity', 0)
        .style('position', 'absolute')
        .style('z-index', '1001')
        .style('background', theme.palette.background.paper)
        .style('color', theme.palette.text.primary)
        .style('border', `1px solid ${alpha(theme.palette.primary.main, 0.5)}`)
        .style('border-radius', '8px')
        .style('padding', '8px')
        .style('pointer-events', 'none')
        .style('font-size', '12px')
        .style('box-shadow', theme.shadows[2]);

      // crosshairs
      const crosshairX = chartArea
        .append('line')
        .attr('class', 'crosshair-x')
        .attr('stroke', theme.palette.text.secondary)
        .attr('stroke-dasharray', '3,3')
        .attr('opacity', 0);

      const crosshairY = chartArea
        .append('line')
        .attr('class', 'crosshair-y')
        .attr('stroke', theme.palette.text.secondary)
        .attr('stroke-dasharray', '3,3')
        .attr('opacity', 0);

      // mouse capture layer
      chartArea
        .append('rect')
        .attr('width', width)
        .attr('height', height)
        .style('fill', 'none')
        .style('pointer-events', 'all')
        .style('cursor', getChartCursor())
        .on('click', () => {
          if (!isFocused && !isMobile) {
            setIsFocused?.(true);
          }
        })
        .on('mousemove', (event: MouseEvent) => {
          const [mx] = d3.pointer(event);
          const traceX = xScale.invert(mx);

          // Find closest data point for each line
          const lineDataPoints = lines.map((line, idx) => {
            const closestPoint = line.data.reduce((a, b) =>
              Math.abs(b.x - traceX) < Math.abs(a.x - traceX) ? b : a,
            );
            return {
              point: closestPoint,
              label: line.label || `Line ${idx + 1}`,
              stroke: line.stroke,
            };
          });

          const mainDataPoint = lineDataPoints[0]?.point || {
            x: traceX,
            y: 0,
            attributes: [],
          };

          // Build tooltip content
          let tooltipContent = `<div style="margin-bottom:4px;font-weight:600;">${xAxisLabel || 'X'}: ${formatAsCurrency(mainDataPoint.x)}</div>`;

          // Add profit/loss lines
          lineDataPoints.forEach((lineData) => {
            tooltipContent += `<div style="display:flex;align-items:center;margin-bottom:2px;">
              <div style="width:12px;height:2px;background-color:${lineData.stroke};margin-right:6px;border-radius:1px;"></div>
              <span style="font-weight:500;">${lineData.label}:</span>
              <span style="margin-left:4px;color:${lineData.stroke};">${formatAsCurrency(lineData.point.y)}</span>
            </div>`;
          });

          // Add Greeks if available
          if (mainDataPoint.attributes?.length > 0) {
            mainDataPoint.attributes.forEach((attr) => {
              tooltipContent += `<div style="margin-bottom:2px;">
                <span>${attr.label}:</span>
                <span style="margin-left:4px;color:${attr.color};">${formatAsCompactNumber(attr.value, { maximumFractionDigits: 3 })}</span>
              </div>`;
            });
          }

          // Show tooltip
          tooltip.html(tooltipContent).style('opacity', 1);

          // Position tooltip within container bounds
          const container = containerRef.current!;
          const containerRect = container.getBoundingClientRect();

          let tooltipX = event.clientX - containerRect.left + 10;

          let tooltipY = event.clientY - containerRect.top - 10;

          const tooltipNode = tooltip.node() as HTMLDivElement;
          const tooltipRect = tooltipNode.getBoundingClientRect();

          tooltipX = Math.max(
            0,
            Math.min(tooltipX, containerRect.width - tooltipRect.width),
          );
          tooltipY = Math.max(
            0,
            Math.min(tooltipY, containerRect.height - tooltipRect.height),
          );

          tooltip.style('left', `${tooltipX}px`).style('top', `${tooltipY}px`);

          // Show crosshairs
          crosshairX
            .attr('x1', xScale(mainDataPoint.x))
            .attr('x2', xScale(mainDataPoint.x))
            .attr('y1', 0)
            .attr('y2', height)
            .attr('opacity', 1);

          crosshairY
            .attr('x1', 0)
            .attr('x2', width)
            .attr('y1', yScale(mainDataPoint.y))
            .attr('y2', yScale(mainDataPoint.y))
            .attr('opacity', 1);
        })
        .on('mouseleave', () => {
          tooltip.style('opacity', 0);
          crosshairX.attr('opacity', 0);
          crosshairY.attr('opacity', 0);
        });

      return () => {
        removeTooltip();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      width,
      height,
      xRange,
      yRange,
      lines,
      areas,
      xAxisLabel,
      yAxisLabel,
      margin,
      showSummary,
      summary,
      referenceLines,
      showReferenceLines,
      theme.palette,
      positiveColor,
      negativeColor,
      isFocused,
      isMobile,
      xScale,
      yScale,
      removeTooltip,
      getChartCursor,
      setIsFocused,
      sym,
      timeToExpiration,
      latestLegExpiration,
      currentPrice,
      theme.shadows,
    ]);

    return (
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
        }}
      >
        {!isFocused && !isMobile && (
          <div
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              zIndex: 10,
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              borderRadius: '8px',
              padding: '8px 12px',
              color: theme.palette.text.secondary,
              fontSize: '11px',
              fontWeight: '500',
              letterSpacing: '0.5px',
              boxShadow: `0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.1)'}, 0 1px 3px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)'}`,
              pointerEvents: 'none',
              opacity: 0.9,
              backdropFilter: 'blur(4px)',
              transition: 'all 0.2s ease-in-out',
            }}
          >
            ✨ Click to Enable Chart Zoom/Pan
          </div>
        )}
        <div
          ref={ref}
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <svg
            ref={svgRef}
            style={{
              width: '100%',
              height: '100%',
              cursor: getChartCursor(),
              overflow: 'hidden',
              display: 'block',
              opacity: 1,
            }}
            viewBox={
              canvasWidth && canvasHeight
                ? `0 0 ${canvasWidth} ${canvasHeight}`
                : undefined
            }
            preserveAspectRatio="xMinYMin meet"
          />

          {/* Strike Picker Overlay */}
          {showStrikePicker && optionLegs.length > 0 && (
            <div
              ref={strikePickerRef}
              style={{
                position: 'absolute',
                left: `${margin.left}px`,
                top: `${margin.top}px`,
                width: `${width}px`,
                height: `${height}px`,
                pointerEvents: 'none',
                zIndex: 5,
              }}
            >
              <DndContext
                sensors={sensors}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={optionLegs.map((leg) => leg.leg_id)}
                  strategy={horizontalListSortingStrategy}
                >
                  {/* Strike slots for drop targets - show all available strikes for active leg's expiry */}
                  {activeLeg &&
                    availableStrikesByExpiry
                      .get(activeLeg.expiry)
                      ?.map((strike) => (
                        <StrikeSlot
                          key={`slot-${activeLeg.expiry}-${strike}`}
                          strike={strike}
                          expiry={activeLeg.expiry}
                          xScale={xScale}
                          height={height}
                          theme={theme}
                          isActive={true}
                        />
                      ))}

                  {/* Strike indicators for bought legs (above x-axis) - stacked */}
                  {Array.from(boughtLegGroups.entries()).map(([, legs]) =>
                    legs.map((leg, index) => (
                      <StrikeIndicator
                        key={`bought-${leg.leg_id}`}
                        leg={leg}
                        placement="above"
                        xScale={xScale}
                        height={height}
                        positiveColor={positiveColor}
                        negativeColor={negativeColor}
                        stackIndex={index}
                        isFirstInStack={index === 0}
                        style={{
                          visibility:
                            activeLeg?.leg_id === leg.leg_id
                              ? 'hidden'
                              : 'visible',
                        }}
                        onLegChange={onLegChange}
                        disabledExpirationOptions={disabledExpirationOptions}
                        expirationOptions={expirationOptions}
                        onLegDelete={onLegDelete}
                      />
                    )),
                  )}

                  {/* Strike indicators for sold legs (below x-axis) - stacked */}
                  {Array.from(soldLegGroups.entries()).map(([, legs]) =>
                    legs.map((leg, index) => (
                      <StrikeIndicator
                        key={`sold-${leg.leg_id}`}
                        leg={leg}
                        placement="below"
                        xScale={xScale}
                        height={height}
                        positiveColor={positiveColor}
                        negativeColor={negativeColor}
                        stackIndex={index}
                        isFirstInStack={index === 0}
                        style={{
                          visibility:
                            activeLeg?.leg_id === leg.leg_id
                              ? 'hidden'
                              : 'visible',
                        }}
                        onLegChange={onLegChange}
                        disabledExpirationOptions={disabledExpirationOptions}
                        expirationOptions={expirationOptions}
                        onLegDelete={onLegDelete}
                      />
                    )),
                  )}
                </SortableContext>

                <DragOverlay>
                  {activeLeg ? (
                    <div
                      style={{
                        position: 'relative',
                        backgroundColor:
                          activeLeg.quantity > 0
                            ? positiveColor
                            : negativeColor,
                        color: theme.palette.getContrastText(
                          activeLeg.quantity > 0
                            ? positiveColor
                            : negativeColor,
                        ),
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        boxShadow: theme.shadows[3],
                        border: `1px solid ${activeLeg.quantity > 0 ? positiveColor : negativeColor}`,
                        transform: 'scale(1.05)',
                        transition: 'all 0.2s ease-in-out',
                        zIndex: 1,
                      }}
                    >
                      {/* Arrow pointing to strike - centered on the box */}
                      <div
                        style={{
                          position: 'absolute',
                          left: '50%',
                          ...(activeLeg.quantity > 0
                            ? {
                                bottom: '-6px',
                                transform: 'translateX(-50%)',
                                width: 0,
                                height: 0,
                                borderLeft: '6px solid transparent',
                                borderRight: '6px solid transparent',
                                borderTop: `6px solid ${positiveColor}`,
                              }
                            : {
                                top: '-6px',
                                transform: 'translateX(-50%)',
                                width: 0,
                                height: 0,
                                borderLeft: '6px solid transparent',
                                borderRight: '6px solid transparent',
                                borderBottom: `6px solid ${negativeColor}`,
                              }),
                        }}
                      />

                      {/* Expiration tag */}
                      {activeLeg.expiry && (
                        <div
                          style={{
                            position: 'absolute',
                            ...(activeLeg.quantity > 0
                              ? { top: -8, right: -4 }
                              : { bottom: -8, right: -4 }),
                            backgroundColor: theme.palette.info.main,
                            color: theme.palette.info.contrastText,
                            fontSize: '0.5rem',
                            fontWeight: 'bold',
                            padding: '1px 3px',
                            borderRadius: '3px',
                            lineHeight: 1,
                            minWidth: '16px',
                            textAlign: 'center',
                            boxShadow: theme.shadows[1],
                            border: `1px solid ${theme.palette.background.paper}`,
                          }}
                        >
                          {formatStrikeIndicatorExpiration(activeLeg.expiry)}
                        </div>
                      )}
                      {hoveredStrike != null ? hoveredStrike : activeLeg.strike}
                      {activeLeg.option_type}
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default D3Plot;
